import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { TrendingUp, Briefcase, Activity, Gauge, ArrowRight, Flame, Linkedin, Twitter, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function Dashboard() {
  const { user } = useAuth();
  const [snap, setSnap] = useState(null);
  const [memos, setMemos] = useState([]);
  const [trending, setTrending] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/snapshot").then((r) => setSnap(r.data));
    api.get("/intelligence").then((r) => setMemos(r.data.slice(0, 3)));
    api.get("/dashboard/trending?days=7").then((r) => setTrending(r.data.trending || []));
  }, []);

  const metrics = snap
    ? [
        { icon: TrendingUp, label: "Avg Returns", value: `${snap.avg_return_low}–${snap.avg_return_high}%` },
        { icon: Briefcase, label: "Active Deals", value: snap.active_deals },
        { icon: Activity, label: "Capital Flow", value: snap.capital_flow },
        { icon: Gauge, label: "Risk Index", value: snap.risk_index },
      ]
    : [];

  return (
    <DashboardLayout>
      <div className="border-b border-border px-6 md:px-10 py-6 flex items-center justify-between">
        <div>
          <div className="overline gold-text">Terminal</div>
          <h1 className="font-display font-medium text-3xl md:text-4xl tracking-tight mt-1">
            Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening"}
            {user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
          </h1>
        </div>
        <div className="hidden md:flex items-center gap-3 text-xs text-muted-foreground">
          <span className="pulse-dot" style={{ backgroundColor: "hsl(var(--status-live))" }} />
          Markets Live · {new Date().toUTCString().slice(17, 22)} UTC
        </div>
      </div>

      <div className="px-6 md:px-10 py-8 space-y-8">
        {/* Snapshot */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {metrics.map(({ icon: Icon, label, value }) => (
            <div key={label} className="card-institutional p-5" data-testid={`snapshot-${label.toLowerCase().replaceAll(" ", "-")}`}>
              <div className="flex items-center justify-between">
                <div className="overline">{label}</div>
                <Icon className="h-4 w-4 gold-text" strokeWidth={1.5} />
              </div>
              <div className="font-mono text-2xl md:text-3xl mt-3">{value}</div>
            </div>
          ))}
        </div>

        {/* Featured deals */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <span className="overline gold-text">Featured</span>
              <h2 className="font-display font-medium text-xl mt-1">Active Deal Flow</h2>
            </div>
            <button onClick={() => navigate("/deals")} className="text-sm gold-text hover:underline flex items-center gap-1" data-testid="view-all-deals-button">
              View all <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {snap?.featured_deals?.map((d) => (
              <button
                key={d.id}
                onClick={() => navigate(`/deals/${d.id}`)}
                className="card-institutional p-5 text-left"
                data-testid={`featured-deal-${d.id}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="overline">{d.country}</span>
                  <StatusBadge status={d.status} />
                </div>
                <h3 className="font-display font-medium text-lg leading-tight min-h-[3rem]">{d.title}</h3>
                <div className="divider my-4" />
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <div className="overline">Size</div>
                    <div className="font-mono mt-1">{d.size_usd}</div>
                  </div>
                  <div>
                    <div className="overline">Tenor</div>
                    <div className="font-mono mt-1">{d.tenor}</div>
                  </div>
                  <div>
                    <div className="overline">Return</div>
                    <div className="font-mono mt-1 gold-text">{d.return_text}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Trending this week (share-driven social proof loop) */}
        {trending.length > 0 && (
          <div data-testid="trending-widget">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Flame className="h-4 w-4 gold-text" strokeWidth={1.8} />
                <div>
                  <span className="overline gold-text">Trending · 7 days</span>
                  <h2 className="font-display font-medium text-xl mt-1">Most Shared This Week</h2>
                </div>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {trending.slice(0, 3).map((t, idx) => {
                const href =
                  t.type === "deal"
                    ? `/deals/${t.entity_id}`
                    : t.type === "memo"
                    ? `/intelligence/${t.entity_id}`
                    : "/playbooks";
                return (
                  <button
                    key={`${t.type}-${t.entity_id}`}
                    onClick={() => navigate(href)}
                    className="card-institutional p-5 text-left relative overflow-hidden"
                    data-testid={`trending-item-${t.entity_id}`}
                  >
                    <div className="absolute top-4 right-4 font-display font-bold text-3xl text-muted-foreground/20">
                      #{idx + 1}
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        {t.type}
                      </span>
                      <span className="pulse-dot" style={{ backgroundColor: "hsl(var(--gold))" }} />
                    </div>
                    <h3 className="font-display font-medium text-base leading-snug min-h-[3rem] pr-10">
                      {t.title}
                    </h3>
                    <div className="divider my-4" />
                    <div className="flex items-center justify-between text-xs">
                      <div className="font-mono gold-text">{t.shares} shares</div>
                      <div className="flex items-center gap-3 text-muted-foreground">
                        {t.channels.linkedin > 0 && (
                          <span className="inline-flex items-center gap-1 font-mono">
                            <Linkedin className="h-3 w-3" /> {t.channels.linkedin}
                          </span>
                        )}
                        {t.channels.twitter > 0 && (
                          <span className="inline-flex items-center gap-1 font-mono">
                            <Twitter className="h-3 w-3" /> {t.channels.twitter}
                          </span>
                        )}
                        {t.channels.visit > 0 && (
                          <span className="inline-flex items-center gap-1 font-mono">
                            <Eye className="h-3 w-3" /> {t.channels.visit}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-[2fr_1fr] gap-6">
          {/* Intelligence */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <span className="overline gold-text">Intelligence</span>
                <h2 className="font-display font-medium text-xl mt-1">Latest Memos</h2>
              </div>
              <button onClick={() => navigate("/intelligence")} className="text-sm gold-text hover:underline flex items-center gap-1" data-testid="view-all-memos-button">
                View all <ArrowRight className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3">
              {memos.map((m) => (
                <button
                  key={m.id}
                  onClick={() => navigate(`/intelligence/${m.id}`)}
                  className="card-institutional p-5 w-full text-left"
                  data-testid={`dashboard-memo-${m.id}`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="overline gold-text">{m.tag}</span>
                    <span className="text-xs text-muted-foreground">· {m.read_time}</span>
                  </div>
                  <h3 className="font-display font-medium text-base leading-snug">{m.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{m.subtitle}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Activity Feed */}
          <div>
            <div className="mb-4">
              <span className="overline gold-text">Activity</span>
              <h2 className="font-display font-medium text-xl mt-1">System Feed</h2>
            </div>
            <div className="card-institutional divide-y divide-border">
              {snap?.activity?.slice(0, 8).map((a) => (
                <div key={a.id} className="flex items-start gap-3 px-5 py-4" data-testid={`activity-${a.id}`}>
                  <span className="pulse-dot mt-2 shrink-0" style={{ backgroundColor: "hsl(var(--gold))" }} />
                  <div className="text-sm">
                    <div>{a.message}</div>
                    <div className="text-xs text-muted-foreground mt-1 font-mono">
                      {new Date(a.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
